import { useEffect, useState } from "preact/hooks";
import { boardFocusOf, noteTagHash, scrollTagHash, type BoardFocus } from "../shared/mentions.ts";

export type Route =
  /**
   * `focus` (a name tag's push, docs/mentions.md): "#/?note=<id>" opens the board with that note
   * opened, "#/?scroll=<week>" with the week's scroll in view (or its sheet once written).
   */
  | { name: "open"; focus?: BoardFocus }
  | { name: "done" }
  | { name: "schedules" }
  | { name: "task"; id: string }
  /** `luxury` opens the form in luxury mode; the tier is chosen on the form itself. */
  | { name: "newTask"; luxury?: boolean }
  | { name: "newSchedule" }
  | { name: "schedule"; id: string }
  | { name: "hall" }
  | { name: "rewards" }
  | { name: "usage" }
  /** Run the house (docs/members.md "The clients"): a steward's screen, opened from Profile > Household. */
  | { name: "house" }
  /** The character editor (docs/peon-look.md): your peon's shirt, hair, beard and extras, opened from your portrait in the account sheet. */
  | { name: "peon" }
  /**
   * Dynasty upgrades (docs/upgrades.md), behind the upgradesUI flag. `wood` opens the tab with its
   * Wood card open (docs/wood.md), for the "Get wood" link on an upgrade; the tabs use the plain route.
   */
  | { name: "upgrades"; wood?: boolean }
  | { name: "newUpgrade" }
  | { name: "upgrade"; id: string }
  /** Trinkets (docs/trinkets.md), behind the trinketsUI flag: the household's collection, opened from Profile. */
  /** `all` (#/trinkets?all, Dev tools' "See every trinket"): every tile drawn, found or not, on this device only. */
  | { name: "trinkets"; all?: boolean }
  /**
   * Suggest something (docs/feedback.md), behind the feedbackUI flag: What's new's "Try it out here"
   * opens its form (src/components/FeedbackSheet.tsx) over the screen the person was on, and sending
   * or cancelling goes back there (src/app.tsx). Profile's About opens the same form inside the sheet.
   */
  | { name: "feedback" };

export function parseHash(hash: string): Route {
  const [rawPath, query] = hash.replace(/^#\/?/, "").split("?");
  const [head, id] = rawPath.split("/");
  switch (head) {
    case "done":
      return { name: "done" };
    case "schedules":
      return { name: "schedules" };
    case "task":
      return id ? { name: "task", id } : { name: "open" };
    case "new":
      return query && new URLSearchParams(query).has("luxury") ? { name: "newTask", luxury: true } : { name: "newTask" };
    case "schedule":
      return id === "new" ? { name: "newSchedule" } : id ? { name: "schedule", id } : { name: "schedules" };
    case "hall":
      return { name: "hall" };
    case "rewards":
      return { name: "rewards" };
    case "usage":
      return { name: "usage" };
    case "house":
      return { name: "house" };
    case "peon":
      return { name: "peon" };
    case "upgrades":
      if (id === "new") return { name: "newUpgrade" };
      return query && new URLSearchParams(query).has("wood") ? { name: "upgrades", wood: true } : { name: "upgrades" };
    case "upgrade":
      return id ? { name: "upgrade", id } : { name: "upgrades" };
    case "trinkets":
      return query && new URLSearchParams(query).has("all") ? { name: "trinkets", all: true } : { name: "trinkets" };
    case "feedback":
      return { name: "feedback" };
    default: {
      const focus = head ? null : boardFocusOf(query);
      return focus ? { name: "open", focus } : { name: "open" };
    }
  }
}

export function href(route: Route): string {
  switch (route.name) {
    case "open":
      return route.focus ? ("note" in route.focus ? noteTagHash(route.focus.note) : scrollTagHash(route.focus.scroll)) : "#/";
    case "done":
      return "#/done";
    case "schedules":
      return "#/schedules";
    case "task":
      return `#/task/${route.id}`;
    case "newTask":
      return route.luxury ? "#/new?luxury" : "#/new";
    case "newSchedule":
      return "#/schedule/new";
    case "schedule":
      return `#/schedule/${route.id}`;
    case "hall":
      return "#/hall";
    case "rewards":
      return "#/rewards";
    case "usage":
      return "#/usage";
    case "house":
      return "#/house";
    case "peon":
      return "#/peon";
    case "upgrades":
      return route.wood ? "#/upgrades?wood" : "#/upgrades";
    case "newUpgrade":
      return "#/upgrades/new";
    case "upgrade":
      return `#/upgrade/${route.id}`;
    case "trinkets":
      return route.all ? "#/trinkets?all" : "#/trinkets";
    case "feedback":
      return "#/feedback";
  }
}

/**
 * Where a link to the site should land inside the Android app (src/native-android.ts): its path,
 * query and hash when it is a link to one of `origins` (the site's, and the page's own when that
 * differs, as in a test build against a local server), else null (another site is none of the
 * app's business). The hash is the route; the query carries a notification's h= and src=push markers.
 */
export function shellLinkTarget(url: string, origins: string | readonly string[]): { path: string; search: string; hash: string } | null {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  if (!(typeof origins === "string" ? [origins] : origins).includes(u.origin)) return null;
  return { path: u.pathname || "/", search: u.search, hash: u.hash };
}

export function navigate(route: Route, replace = false): void {
  const h = href(route);
  if (replace) history.replaceState(null, "", h);
  else location.hash = h;
  if (replace) window.dispatchEvent(new HashChangeEvent("hashchange"));
}

export function back(fallback: Route): void {
  if (history.length > 1) history.back();
  else navigate(fallback, true);
}

/**
 * The route shown before the current one, for a screen that behaves differently on a return than on
 * a fresh open (a board restores its scroll coming back from a task, src/views/Board.tsx). Tracked
 * by one listener registered when this module loads, so it is current before any screen re-renders.
 */
let currentHash = typeof location === "undefined" ? "" : location.hash;
let previousHash: string | null = null;
if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener("hashchange", () => {
    if (location.hash === currentHash) return;
    previousHash = currentHash;
    currentHash = location.hash;
  });
}

/** The name of the route shown before this one, or null at launch. */
export function previousRouteName(): Route["name"] | null {
  return previousHash === null ? null : parseHash(previousHash).name;
}

// Screens switch in one frame, on purpose. A view transition was tried (v135, v136) and reverted:
// snapshotting the root made iOS re-tint the status bar mid-transition, and animating only the
// content pane left the tab bar missing from the frame while it ran. The smoothness comes from
// painting screens from cache instead (src/board-cache.ts), so the switch itself has nothing to hide.
export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseHash(location.hash));
  useEffect(() => {
    const onChange = () => setRoute(parseHash(location.hash));
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return route;
}
